import { useCallback, useEffect, useRef } from 'react';
import { catchUpConfig } from '../config';

/**
 * Catch-up logic hook.
 *
 * When the player falls behind the live edge by more than 2x segment duration
 * (3.84s), playback speed increases to 1.4x. When the player catches up to
 * within 1x segment duration (1.92s), speed returns to 1.0x.
 *
 * Requirements:
 * - Trigger catch-up when delay > 3.84s (Req 1.11)
 * - Cap speed at 1.4x (Req 1.12)
 * - Resume normal at < 1.92s delay (Req 1.13)
 */
export function useCatchUp(videoRef: React.RefObject<HTMLVideoElement | null>) {
  const isCatchingUp = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkDelay = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.paused || !video.buffered.length) return;

    // Calculate delay from live edge
    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
    const currentTime = video.currentTime;
    const delay = bufferedEnd - currentTime;

    if (delay > catchUpConfig.maxDelay && !isCatchingUp.current) {
      // Behind live edge — speed up
      video.playbackRate = catchUpConfig.catchUpSpeed;
      isCatchingUp.current = true;
    } else if (delay <= catchUpConfig.resumeThreshold && isCatchingUp.current) {
      // Caught up — resume normal speed
      video.playbackRate = 1.0;
      isCatchingUp.current = false;
    }
  }, [videoRef]);

  useEffect(() => {
    // Check delay every 500ms
    intervalRef.current = setInterval(checkDelay, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkDelay]);

  return { isCatchingUp: isCatchingUp.current };
}
